import {
  Button,
  ButtonProps,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { forwardRef, RefObject } from 'react'
import { TriangleUnderIcon } from '../Icons/TriangleUnderIcon'

type Props = {
  label: string | undefined
  defaultLabel: string
  initialFocusRef?: RefObject<HTMLInputElement>
  isValid: boolean
  hasFooter?: boolean
  onOk?: VoidFunction
  onCancel?: VoidFunction
  onOpen?: VoidFunction
}
export const QueryPopover: React.FC<ButtonProps & Props> = ({
  children,
  initialFocusRef,
  isValid,
  hasFooter = true,
  onCancel,
  onOk,
  onOpen,
  ...buttonProps
}) => {
  const { isOpen, onOpen: onOpenPopover, onClose } = useDisclosure()
  const onClosePopOver = () => {
    onClose()
    onCancel?.()
  }
  const onSubmit = () => {
    onOk?.()
    onClose()
  }
  return (
    <Popover
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={onClosePopOver}
    >
      <PopoverTrigger>
        <QueryPopoverButton
          {...buttonProps}
          onClick={() => {
            onOpen?.()
            onOpenPopover()
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        w="min-content"
        _focus={{
          boxShadow: 'none'
        }}
      >
        <PopoverBody
          bgColor="white.100"
          p="0"
          onKeyDown={(event) => {
            if (event.keyCode === 229) {
              event.preventDefault()
              event.stopPropagation()

              return
            }
            if (event?.key?.toLowerCase() === 'enter') {
              event.preventDefault()
              event.stopPropagation()
              onSubmit()
            }
          }}
        >
          {children}
        </PopoverBody>
        {hasFooter && (
          <PopoverFooter bgColor="white.100" py="12px" px="16px">
            <Flex gap="10px" justify="flex-end">
              <Button
                minH="30px"
                h="30px"
                fontSize="13px"
                lineHeight="13px"
                borderRadius="2px"
                variant="outline"
                borderColor="purple.200"
                py="8.5"
                px="12px"
                onClick={onClosePopOver}
              >
                キャンセル
              </Button>
              <Button
                bgColor="primary.purple"
                minH="30px"
                h="30px"
                fontSize="13px"
                lineHeight="13px"
                color="white.100"
                borderRadius="2px"
                py="8.5"
                px="12px"
                onClick={onSubmit}
                isDisabled={!isValid}
              >
                OK
              </Button>
            </Flex>
          </PopoverFooter>
        )}
      </PopoverContent>
    </Popover>
  )
}

export const QueryPopoverButton = forwardRef<
  HTMLButtonElement,
  {
    label: string | undefined
    defaultLabel: string
    isFiltered?: boolean
  } & ButtonProps
>(({ label, defaultLabel, isFiltered, ...buttonProps }, ref) => {
  const _isFiltered = isFiltered ?? label !== undefined
  return (
    <Button
      gap="1"
      variant="outline"
      _hover={{
        bgColor: _isFiltered ? 'navy.primary' : 'white.100'
      }}
      boxShadow="0px 2px 4px 0px rgba(0, 0, 0, 0.06)"
      pl="2"
      pr="3"
      py="0"
      size="sm"
      minH="26px"
      h="26px"
      fontWeight={500}
      color={_isFiltered ? 'white.100' : 'navy.500'}
      bgColor={_isFiltered ? 'navy.primary' : 'white.100'}
      borderRadius="24px"
      borderColor={_isFiltered ? 'navy.primary' : 'gray.200'}
      {...buttonProps}
      ref={ref}
    >
      <TriangleUnderIcon width="16px" height="16px" />
      <Text maxW="200px" noOfLines={1}>
        {label ?? defaultLabel}
      </Text>
    </Button>
  )
})
